import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { DatePicker } from 'antd';

import Table from 'components/Table';
import { Typography, Collapse, Button, Tooltip } from 'ui-kit';

import { useOwnerDetails } from 'api/owners';
import { useOwnerPayoutNote, useOwnerPayouts } from 'api/owner_sdk';
import { useSignedUser } from 'api/auth';
import { PaymentPlan } from 'api/types/owner';

import { formatCurrency } from 'utils';
import { getFirstAndLastDayOfCurrentMonth, MONTHS_MAP } from 'utils/datetime';
import { hasMinimumPlan } from 'utils/owner';

import PayoutCalculatorPopover from './PayoutCalculatorPopover';
import OrderProductDetailsModal from './OrderProductDetailsModal';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import './PayoutList.scss';
import PayoutsExportButton from './PayoutsExportButton';

const { RangePicker } = DatePicker;

const PayoutStatus = {
  // Payout with statuses Pending and Processing does not exist anymore
  PAYING: 'Paying',
  COMP: 'Completed',
  FAIL: 'Failed',
  CNCL: 'Cancelled',
  STRIPE: 'Stripe',
};

const NOW = new Date();

const PayoutsList = () => {
  const { firstDayOfMonth, lastDayOfMonth } = getFirstAndLastDayOfCurrentMonth(NOW);

  const [startDate, setStartDate] = useState<Date>(firstDayOfMonth);
  const [endDate, setEndDate] = useState<Date>(lastDayOfMonth);
  const [params, setParams] = useState({ startDate, endDate });

  // Calculator
  const [showCalculator, setShowCalculator] = useState(false);
  const [openCalculator, setOpenCalculator] = useState(false);
  const [openPreviewCalculator, setOpenPreviewCalculator] = useState(false);
  const [selectedStoreForCalculation, setSelectedStoreForCalculation] = useState(null);
  const [selectedRowIndexForCalculation, setSelectedRowIndexForCalculation] = useState(null);
  const [ownerPayouts, setOwnerPayouts] = useState(null);

  // Show Order Product details V2
  const [currentRecord, setCurrentRecord] = useState(null);
  const [visibleModal, setVisibleModal] = useState(false);

  const {
    data: ownerPayoutDetails,
    mutate: updateOwnerPayoutDetails,
    isLoading,
  } = useOwnerPayouts(params);
  const { data: ownerPayoutNote } = useOwnerPayoutNote();
  const { data: user } = useSignedUser();
  const { data: owner } = useOwnerDetails(user?.owner_id);

  useEffect(() => {
    if (!_.isUndefined(owner) && hasMinimumPlan(owner.plan, PaymentPlan.ProPlus)) {
      setShowCalculator(true);
    }
  }, [owner]);

  useEffect(() => {
    setParams({ startDate, endDate });
  }, [startDate, endDate]);

  const onChangeRange = (arr) => {
    const start = new Date(arr[0]);
    const end = new Date(arr[1]);
    setStartDate(start);
    const { lastDayOfMonth } = getFirstAndLastDayOfCurrentMonth(end);
    setEndDate(lastDayOfMonth);
  };

  const handleOpenCalulcator = useCallback(
    ({
      storeId,
      rowIndex,
      initialOwnerProfitPercentage,
    }: {
      storeId: number;
      rowIndex: number;
      initialOwnerProfitPercentage: number;
    }) => {
      setOpenCalculator(true);
      setSelectedRowIndexForCalculation(rowIndex);
      setSelectedStoreForCalculation(storeId);
      setOwnerPayouts(initialOwnerProfitPercentage);
    },
    [],
  );

  const handleCloseCalulcator = useCallback(() => {
    setOpenCalculator(false);
    setSelectedStoreForCalculation(null);
    setSelectedRowIndexForCalculation(null);
    setOwnerPayouts(null);
  }, []);

  const handleCalculate = useCallback(() => {
    updateOwnerPayoutDetails();
    handleCloseCalulcator();
  }, [updateOwnerPayoutDetails, handleCloseCalulcator]);

  const totalPayouts = useMemo(
    () => _.sumBy(ownerPayoutDetails, 'total_owner_amount_after_balances'),
    [ownerPayoutDetails],
  );

  const shouldShowPayoutNote = !!ownerPayoutNote?.note && totalPayouts < 15;

  return (
    <>
      <Collapse
        header={() => <Typography variant="h6">Payouts</Typography>}
        initialCollpase={false}
      >
        <RangePicker
          value={[moment(startDate), moment(endDate)]}
          onChange={onChangeRange}
          allowClear={false}
          picker="month"
          format={'MMMM-YYYY'}
          className="PayoutCalendar"
        />

        <div className="DashboardPayouts_body">
          <Table
            loading={isLoading}
            scroll={{ x: '100%' }}
            className="DashboardPayouts_table"
            dataSource={ownerPayoutDetails}
            columns={[
              {
                title: 'Month',
                dataIndex: 'last_date_created',
                key: 'last_date_created',
                render: (text, _record, _index) => (
                  <>{MONTHS_MAP[new Date(text).getUTCMonth() + 1]}</>
                ),
              },
              {
                title: 'Year',
                dataIndex: 'last_date_created',
                key: 'last_date_created',
                render: (text, _record, _index) => <>{new Date(text).getUTCFullYear()}</>,
              },
              {
                title: 'Store',
                dataIndex: 'store',
                key: 'store',
                render: (_text, record) => <>{record.store.store_name}</>,
              },
              {
                title: 'Payout Amount',
                dataIndex: 'total_owner_amount_after_balances',
                key: 'total_owner_amount_after_balances',
                render: (_text, record) => (
                  <>{formatCurrency(record.total_owner_amount_after_balances, record.currency)}</>
                ),
              },
              {
                title: (
                  <>
                    <span>Payout Status</span>{' '}
                    <Tooltip title="Payouts are processed on the 7th of the month and take on average 1-4 business days to arrive. Payouts occur the following month of the service date.">
                      <ExclamationCircleOutlined />
                    </Tooltip>
                  </>
                ),
                dataIndex: 'payouts',
                key: 'payouts',
                render: (_text, record) => (
                  <>
                    {record.order_products_are_paid
                      ? record.payouts.map((p) => PayoutStatus[p.status]).join(', ')
                      : 'Upcoming'}
                  </>
                ),
              },
              {
                render: (_text, record) => (
                  <>
                    <Button
                      onClick={() => {
                        setCurrentRecord(record);
                        setVisibleModal(true);
                      }}
                    >
                      View details
                    </Button>

                    {visibleModal && (
                      <OrderProductDetailsModal
                        owner={owner}
                        storeId={currentRecord?.store.id}
                        payoutStartDate={new Date(currentRecord?.first_date_created)}
                        payoutEndDate={new Date(currentRecord?.last_date_created)}
                        handleClose={() => {
                          setVisibleModal(false);
                          setCurrentRecord(null);
                        }}
                      />
                    )}
                  </>
                ),
              },
              {
                title: (
                  <div>
                    Calculate split{' '}
                    {_.isEmpty(ownerPayoutDetails) && (
                      <PayoutCalculatorPopover
                        isOpen={openPreviewCalculator}
                        previewMode={true}
                        enabledFeature={showCalculator}
                        storeName={''}
                        storeId={selectedStoreForCalculation}
                        initialOwnerProfitPercentage={Number(ownerPayouts)}
                        onOpen={() => setOpenPreviewCalculator(true)}
                        onClose={() => setOpenPreviewCalculator(false)}
                        onCalculate={() => setOpenPreviewCalculator(false)}
                      />
                    )}
                  </div>
                ),
                dataIndex: 'calculateSplit',
                key: 'calculateSplit',
                className: 'DashboardPayouts_calculator',
                render: (_, record, index) => (
                  <PayoutCalculatorPopover
                    key={index}
                    previewMode={false}
                    isOpen={openCalculator && selectedRowIndexForCalculation === index}
                    enabledFeature={showCalculator}
                    storeName={record.store.storeName}
                    storeId={selectedStoreForCalculation}
                    initialOwnerProfitPercentage={Number(ownerPayouts)}
                    onOpen={() =>
                      handleOpenCalulcator({
                        storeId: record.store.id,
                        rowIndex: index,
                        initialOwnerProfitPercentage: record.store.owner_profit_percentage,
                      })
                    }
                    onClose={handleCloseCalulcator}
                    onCalculate={handleCalculate}
                  />
                ),
              },
              {
                title: 'Party A',
                dataIndex: 'ownerPayout',
                key: 'ownerPayout',
                className: 'DashboardPayouts_party',
                render: (_, record) => (
                  <>
                    {showCalculator ? (
                      <Typography variant="body2" color="textGray">
                        {formatCurrency(
                          (Number(record.store.owner_profit_percentage) / 100) *
                            record.total_owner_amount_after_balances,
                          record.currency,
                        )}{' '}
                        ({Number(record.store.owner_profit_percentage).toFixed(2)}%)
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="textGray">
                        -
                      </Typography>
                    )}
                  </>
                ),
              },
              {
                title: 'Party B',
                dataIndex: 'partyBPayout',
                key: 'partyBPayout',
                className: 'DashboardPayouts_party',
                render: (_, record) => (
                  <>
                    {showCalculator ? (
                      <Typography variant="body2" color="textGray">
                        {formatCurrency(
                          ((100 - Number(record.store.owner_profit_percentage)) / 100) *
                            record.total_owner_amount_after_balances,
                          record.currency,
                        )}{' '}
                        ({(100 - Number(record.store.owner_profit_percentage)).toFixed(2)}%)
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="textGray">
                        -
                      </Typography>
                    )}
                  </>
                ),
              },
            ]}
          />

          <div className="DashboardPayouts_totalsAndExport">
            <div>Total payout amount: {formatCurrency(totalPayouts, 'USD')}</div>
            <PayoutsExportButton startDate={startDate} endDate={endDate} />
          </div>
        </div>

        <Typography variant="caption">
          * Total may not be exact due to process timing, partial refunds, and cancellations.
        </Typography>
      </Collapse>

      {shouldShowPayoutNote && (
        <div className="DashboardPayouts_notesWrapper">
          <div className="DashboardPayouts_notesBox">
            <Typography variant="h6">Notes</Typography>
            <div className="DashboardPayouts_notesText">{ownerPayoutNote.note}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default PayoutsList;
